import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { ApiError } from '~/api/client';
import type { RiskLevel, ScanVerdict, Severity } from '~/api/types';

/**
 * The shared visual vocabulary.
 *
 * Kept small and unstyled-at-the-callsite on purpose: risk levels, security verdicts and install
 * statuses all need to look the same everywhere they appear, and a marketplace where "high risk"
 * is amber on one page and red on another has taught the user nothing.
 */

export function classes(...values: readonly (string | false | undefined)[]): string {
  return values.filter((value): value is string => typeof value === 'string' && value !== '').join(' ');
}

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

const BUTTON_STYLES: Readonly<Record<ButtonVariant, string>> = {
  primary:
    'bg-brand text-slate-950 hover:bg-brand-soft font-semibold shadow-glow hover:shadow-[0_12px_36px_-12px_rgba(246,130,31,0.55)]',
  secondary:
    'bg-slate-800/90 text-slate-100 hover:bg-slate-700 border border-slate-700/90 hover:border-slate-600',
  ghost: 'text-slate-300 hover:bg-slate-800/80 hover:text-slate-100',
  danger: 'bg-red-600/90 text-white hover:bg-red-500 shadow-sm shadow-red-950/40',
};

export function Button({
  variant = 'secondary',
  isLoading = false,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  isLoading?: boolean;
}): JSX.Element {
  return (
    <button
      {...rest}
      disabled={rest.disabled === true || isLoading}
      aria-busy={isLoading}
      className={classes(
        'inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm transition-all duration-200 ease-soft disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none',
        BUTTON_STYLES[variant],
        className,
      )}
    >
      {isLoading ? <Spinner /> : null}
      {children}
    </button>
  );
}

export function Spinner({ className }: { className?: string }): JSX.Element {
  return (
    <span
      aria-hidden="true"
      className={classes(
        'inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent',
        className,
      )}
    />
  );
}

export function Badge({
  children,
  tone = 'neutral',
  title,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'good' | 'warn' | 'bad' | 'brand';
  title?: string;
}): JSX.Element {
  const tones = {
    neutral: 'bg-slate-800 text-slate-300 border-slate-700',
    good: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
    warn: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
    bad: 'bg-red-500/10 text-red-300 border-red-500/30',
    brand: 'bg-brand/10 text-brand-soft border-brand/30',
  } as const;

  return (
    <span
      title={title}
      className={classes(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
        tones[tone],
      )}
    >
      {children}
    </span>
  );
}

const RISK_LABEL: Readonly<Record<RiskLevel, string>> = {
  LOW: 'Low risk',
  MEDIUM: 'Medium risk',
  HIGH: 'High risk',
};

export function RiskBadge({ risk }: { risk: RiskLevel }): JSX.Element {
  const tone = risk === 'LOW' ? 'good' : risk === 'MEDIUM' ? 'warn' : 'bad';
  return (
    <Badge tone={tone} title="Based on the most sensitive permission this application requests">
      {RISK_LABEL[risk]}
    </Badge>
  );
}

/**
 * The security verdict, phrased for a person rather than a scanner.
 *
 * "PASSED_WITH_WARNINGS" means nothing to someone deciding whether to install something; "Minor
 * warnings" does (§25).
 */
const VERDICT_TEXT: Readonly<Record<ScanVerdict, { label: string; tone: 'good' | 'warn' | 'bad' }>> =
  {
    PASSED: { label: 'Security checks passed', tone: 'good' },
    PASSED_WITH_WARNINGS: { label: 'Minor warnings', tone: 'warn' },
    NEEDS_REVIEW: { label: 'Awaiting review', tone: 'warn' },
    BLOCKED: { label: 'Blocked by security review', tone: 'bad' },
  };

export function VerdictBadge({ verdict }: { verdict: ScanVerdict | undefined }): JSX.Element {
  if (verdict === undefined) return <Badge tone="neutral">Not yet analyzed</Badge>;
  const { label, tone } = VERDICT_TEXT[verdict];
  return <Badge tone={tone}>{label}</Badge>;
}

export const SEVERITY_TONE: Readonly<Record<Severity, 'neutral' | 'good' | 'warn' | 'bad'>> = {
  INFO: 'neutral',
  LOW: 'neutral',
  MEDIUM: 'warn',
  HIGH: 'bad',
  CRITICAL: 'bad',
};

export function Stars({ average, count }: { average: number; count: number }): JSX.Element {
  const rounded = Math.round(average);
  return (
    <span className="inline-flex items-center gap-1 text-sm text-slate-400">
      <span aria-hidden="true" className="text-amber-400">
        {'★'.repeat(rounded)}
        <span className="text-slate-700">{'★'.repeat(5 - rounded)}</span>
      </span>
      <span className="sr-only">{`Rated ${average.toFixed(1)} out of 5`}</span>
      {count === 0 ? (
        <span>No reviews</span>
      ) : (
        <span>
          {average.toFixed(1)} ({formatCount(count)})
        </span>
      )}
    </span>
  );
}

export function Section({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="mb-14 animate-fade-up">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div className="max-w-2xl">
          <h2 className="font-display text-xl font-bold tracking-tight text-slate-50">{title}</h2>
          {description === undefined ? null : (
            <p className="mt-1.5 text-sm leading-relaxed text-slate-400">{description}</p>
          )}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

/**
 * The error state for a failed load.
 *
 * Shows the API's own message, because those messages are written to be read by the person who hit
 * the problem, and the request id, because that is what makes a support conversation solvable.
 */
export function ErrorPanel({
  error,
  onRetry,
}: {
  error: ApiError;
  onRetry?: () => void;
}): JSX.Element {
  return (
    <div role="alert" className="card border-red-500/30 bg-red-500/5">
      <p className="font-medium text-red-200">{error.message}</p>
      {Object.entries(error.fields).length > 0 ? (
        <ul className="mt-2 space-y-1 text-sm text-red-200/80">
          {Object.entries(error.fields).map(([field, message]) => (
            <li key={field}>
              <span className="font-mono text-xs">{field}</span>: {message}
            </li>
          ))}
        </ul>
      ) : null}
      <div className="mt-3 flex items-center gap-3">
        {onRetry !== undefined && error.isRetryable ? (
          <Button variant="secondary" onClick={onRetry}>
            Try again
          </Button>
        ) : null}
        {error.requestId === undefined ? null : (
          <span className="font-mono text-xs text-slate-500">Reference {error.requestId}</span>
        )}
      </div>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}): JSX.Element {
  return (
    <div className="card flex flex-col items-center gap-3 py-16 text-center">
      <div
        aria-hidden="true"
        className="grid h-12 w-12 place-items-center rounded-2xl border border-slate-700 bg-slate-950/60 text-lg text-slate-500"
      >
        ◌
      </div>
      <h3 className="font-display text-base font-bold text-slate-100">{title}</h3>
      <p className="max-w-md text-sm leading-relaxed text-slate-400">{description}</p>
      {action === undefined ? null : <div className="mt-2">{action}</div>}
    </div>
  );
}

/** Placeholder blocks sized like the content they replace, so the page does not jump on load. */
export function SkeletonGrid({ count = 6 }: { count?: number }): JSX.Element {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="card animate-pulse">
          <div className="h-10 w-10 rounded-lg bg-slate-800" />
          <div className="mt-4 h-4 w-2/3 rounded bg-slate-800" />
          <div className="mt-2 h-3 w-full rounded bg-slate-800/70" />
          <div className="mt-1.5 h-3 w-4/5 rounded bg-slate-800/70" />
        </div>
      ))}
    </div>
  );
}

export function PageHeading({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string | undefined;
  action?: ReactNode | undefined;
}): JSX.Element {
  return (
    <header className="mb-10 flex flex-wrap items-end justify-between gap-4">
      <div className="max-w-2xl">
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-50">{title}</h1>
        {subtitle === undefined ? null : (
          <p className="mt-2 text-sm leading-relaxed text-slate-400">{subtitle}</p>
        )}
      </div>
      {action}
    </header>
  );
}

export function Breadcrumbs({
  trail,
}: {
  trail: readonly { label: string; to?: string }[];
}): JSX.Element {
  return (
    <nav aria-label="Breadcrumb" className="mb-6 text-sm text-slate-500">
      <ol className="flex flex-wrap items-center gap-2">
        {trail.map((entry, index) => (
          <li key={entry.label} className="flex items-center gap-2">
            {index > 0 ? <span aria-hidden="true">/</span> : null}
            {entry.to === undefined ? (
              <span className="text-slate-300">{entry.label}</span>
            ) : (
              <Link to={entry.to} className="hover:text-slate-200">
                {entry.label}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function formatCount(value: number): string {
  if (value < 1000) return String(value);
  if (value < 1_000_000) return `${(value / 1000).toFixed(value < 10_000 ? 1 : 0)}k`;
  return `${(value / 1_000_000).toFixed(1)}M`;
}

export function formatDate(iso: string | undefined): string {
  if (iso === undefined) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** "3 days ago" reads better than a date for things that change often, like install progress. */
export function formatRelative(iso: string | undefined): string {
  if (iso === undefined) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';

  const seconds = Math.round((Date.now() - then) / 1000);
  const units: readonly [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['day', 86_400],
    ['hour', 3600],
    ['minute', 60],
  ];
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) return formatter.format(-Math.round(seconds / size), unit);
  }
  return formatter.format(-seconds, 'second');
}
